<div class="min-h-screen">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div class="bg-white p-6 rounded-xl shadow-sm">
            <div class="flex items-center justify-between">
                <div>
                    <p class="text-gray-500 text-sm">Production Deployments by Team</p>
                    @if (teamChartLabels) {
                        <apx-chart
                            [series]="teamChartData"
                            [chart]="chartOptions.chart"
                            [labels]="teamChartLabels"
                            [responsive]="chartOptions.responsive"
                            [legend]="chartOptions.legend"
                            [dataLabels]="chartOptions.dataLabels"
                            [plotOptions]="chartOptions.plotOptions"
                        ></apx-chart>
                    }
                </div>
            </div>
        </div>

        <div class="bg-white p-6 rounded-xl shadow-sm">
            <div class="flex items-center justify-between">
                <div>
                    <p class="text-gray-500 text-sm">Production Deployments</p>
                    @if (deployments) {
                        <p class="text-3xl font-bold mt-2">{{ deployments.length }}</p>
                    }
                </div>
                <div class="bg-yellow-100 p-3 rounded-full">
                    <svg class="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                </div>
            </div>
        </div>
    </div>

    <div></div>

    <!-- Recent Deployments Table -->
    <div class="bg-white rounded-xl shadow-sm overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-200">
            <h3 class="text-lg font-semibold">Recent Deployments</h3>
        </div>

        <ul role="list" class="divide-y divide-gray-200">
            @for (deployment of deployments; track deployment) {
                <li class="relative flex items-center space-x-4 px-4 py-4 sm:px-6 lg:px-8">
                    <div class="min-w-0 flex-auto">
                        <div class="flex items-center gap-x-3">
                            <div class="p-1">
                                <svg width="16" height="16" viewBox="0 0 1024 1024" fill="none">
                                    <path
                                        fill-rule="evenodd"
                                        clip-rule="evenodd"
                                        d="M8 0C3.58 0 0 3.58 0 8C0 11.54 2.29 14.53 5.47 15.59C5.87 15.66 6.02 15.42 6.02 15.21C6.02 15.02 6.01 14.39 6.01 13.72C4 14.09 3.48 13.23 3.32 12.78C3.23 12.55 2.84 11.84 2.5 11.65C2.22 11.5 1.82 11.13 2.49 11.12C3.12 11.11 3.57 11.7 3.72 11.94C4.44 13.15 5.59 12.81 6.05 12.6C6.12 12.08 6.33 11.73 6.56 11.53C4.78 11.33 2.92 10.64 2.92 7.58C2.92 6.71 3.23 5.99 3.74 5.43C3.66 5.23 3.38 4.41 3.82 3.31C3.82 3.31 4.49 3.1 6.02 4.13C6.66 3.95 7.34 3.86 8.02 3.86C8.7 3.86 9.38 3.95 10.02 4.13C11.55 3.09 12.22 3.31 12.22 3.31C12.66 4.41 12.38 5.23 12.3 5.43C12.81 5.99 13.12 6.7 13.12 7.58C13.12 10.65 11.25 11.33 9.47 11.53C9.76 11.78 10.01 12.26 10.01 13.01C10.01 14.08 10 14.94 10 15.21C10 15.42 10.15 15.67 10.55 15.59C13.71 14.53 16 11.53 16 8C16 3.58 12.42 0 8 0Z"
                                        transform="scale(64)"
                                        fill="#1B1F23"
                                    />
                                </svg>
                            </div>
                            <h2 class="min-w-0 text-sm font-semibold leading-6 text-gray-900">
                                <a href="#" routerLink="/deployment/{{ deployment.id }}" class="flex gap-x-2">
                                    <span class="truncate">{{ deployment.team.name }}</span>
                                    <span class="text-gray-500">/</span>
                                    <span class="whitespace-nowrap">{{ deployment.service.name }}</span>
                                    <span class="text-xs text-gray-500">v{{ deployment.appVersion.version }}</span>
                                    <span class="absolute inset-0" [title]="formatDate(deployment.date)"></span>
                                </a>
                            </h2>
                        </div>
                        <div class="mt-3 flex items-center gap-x-2.5 text-xs leading-5 text-gray-600">
                            <p class="truncate">Deployed from GitHub</p>
                            <svg viewBox="0 0 2 2" class="h-0.5 w-0.5 flex-none fill-gray-400">
                                <circle cx="1" cy="1" r="1" />
                            </svg>
                            <p class="whitespace-nowrap">
                                Initiated
                                <time [dateTime]="deployment.date">{{ formatDistanceToNow(deployment.date) }} ago</time>
                            </p>
                        </div>
                    </div>
                    @if (deployment.stage.name === 'Development') {
                        <div
                            class="rounded-full flex-none py-1 px-2 text-xs font-medium ring-1 ring-inset text-zinc-700 bg-zinc-100 ring-zinc-300"
                        >
                            {{ deployment.stage.name }}
                        </div>
                    }
                    @if (deployment.stage.name === 'Staging') {
                        <div
                            class="rounded-full flex-none py-1 px-2 text-xs font-medium ring-1 ring-inset text-teal-700 bg-teal-100 ring-teal-300"
                        >
                            {{ deployment.stage.name }}
                        </div>
                    }
                    @if (deployment.stage.name === 'Production') {
                        <div
                            class="rounded-full flex-none py-1 px-2 text-xs font-medium ring-1 ring-inset text-indigo-700 bg-indigo-100 ring-indigo-300"
                        >
                            {{ deployment.stage.name }}
                        </div>
                    }
                    <svg class="h-5 w-5 flex-none text-gray-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path
                            fill-rule="evenodd"
                            d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </li>
            }
        </ul>
    </div>
</div>
